/**
 * TaskChatPanel — the task-scoped, Vari-crew-only chat (demo #7).
 *
 * A "Chatter"-style message board on a single task: the crew assigned to the
 * task plus supervisors/admins post short messages and @mention each other. An
 * @mention emails the mentioned Vari users a link back here.
 *
 * This is DELIBERATELY DISTINCT from the requester-facing Updates/activity feed
 * — the PM/requester is never a participant or recipient. The header makes that
 * explicit so nobody mistakes it for client-visible communication.
 *
 * Mounted on the crew task screen (CrewTaskDetail) and in the supervisor task
 * view (ProjectDetail's Tasks list). Mention autocomplete is sourced from the
 * task's eligible participants (crew + supervisors), returned with the list.
 *
 * Beyond person mentions the composer also supports (demo #7):
 *   • "@crew" — a group mention that notifies everyone on the task.
 *   • Attaching images / PDFs straight to a message (paperclip → upload).
 *   • "@"-referencing one of the project's documents — picked from the same
 *     autocomplete, it rides along as a doc chip (it doesn't go into the body).
 * All three render under the message as image thumbnails or file/doc chips.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { format, parseISO } from 'date-fns';
import { AtSign, Camera, FileText, Loader2, Lock, Paperclip, Send, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import type { TaskMessageAttachment, TaskMessageParticipant } from '@vari-crew/schemas';
import { useChatMessages, useSendChatMessage, uploadChatMedia, type ChatScope } from '@/api/chat';
import { attachmentFileUrl, useProjectDocuments } from '@/api/attachments';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

/** The "@token" currently under the caret, if the caret sits inside a mention. */
function activeMentionToken(text: string, caret: number): { query: string; start: number } | null {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  // Must be at the start or preceded by whitespace, and have no whitespace
  // between the "@" and the caret (still typing one token after the handle).
  if (at > 0 && !/\s/.test(upto[at - 1]!)) return null;
  const frag = upto.slice(at + 1);
  if (/\s/.test(frag)) return null;
  return { query: frag.toLowerCase(), start: at };
}

function roleHint(role: string): string {
  if (role === 'supervisor') return 'Supervisor';
  if (role === 'admin') return 'Admin';
  return 'Crew';
}

/** Escape a string for safe use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A document/file pinned to the not-yet-sent message (uploaded or @-referenced). */
interface PendingAttachment {
  id: string;
  filename: string;
  is_image: boolean;
  /** Pre-minted download URL (project docs ship one); falls back to the id route. */
  file_url?: string;
}

export function TaskChatPanel({
  scope,
  className,
}: {
  /** The thread this panel drives — a task (legacy) or a project (D1). */
  scope: ChatScope;
  className?: string;
}) {
  const { data, isLoading } = useChatMessages(scope);
  const send = useSendChatMessage(scope);
  const { data: me } = useCurrentUser();

  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pendingAtts, setPendingAtts] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const messages = data?.data ?? [];
  const participants = useMemo<TaskMessageParticipant[]>(
    () => data?.participants ?? [],
    [data],
  );

  // The task's project — drives the document picker. Available even when the
  // chat has zero messages (the list response carries it top-level).
  const projectId = data?.project_id ?? undefined;
  // Project documents for the "@"-picker. Degrades gracefully: if the endpoint
  // rejects crew (authz), the query simply yields no data and the doc group is
  // omitted — supervisors still get it, and the panel never crashes.
  const { data: docsData } = useProjectDocuments(projectId);
  const projectDocs = docsData?.data ?? [];

  // Live @mention suggestions for the token under the caret.
  const mention = activeMentionToken(text, caret);
  const suggestions = useMemo(() => {
    if (!mention) return [];
    const q = mention.query;
    return participants
      .filter((p) => p.user_id !== me?.id) // can't mention yourself
      .filter((p) => p.display_name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, participants, me?.id]);

  // "@crew" group-mention suggestion — surfaced (as the top item) whenever the
  // typed token is a prefix of "crew", so "@", "@c", "@cre" all offer it.
  const offerCrew = !!mention && 'crew'.startsWith(mention.query);

  // Document suggestions for the same "@"-token (project docs only). Matched on
  // filename; picking one pins it as a chip rather than inserting into the body.
  const docSuggestions = useMemo(() => {
    if (!mention) return [];
    const q = mention.query;
    const chosen = new Set(pendingAtts.map((a) => a.id));
    return projectDocs
      .filter((d) => !chosen.has(d.id)) // don't offer an already-pinned doc
      .filter((d) => d.original_filename.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, projectDocs, pendingAtts]);

  const hasSuggestions = offerCrew || suggestions.length > 0 || docSuggestions.length > 0;

  // Known mention targets (full names + first names, longest-first) so "@Name"
  // in a posted message renders as a pill instead of getting lost in the text.
  // "@crew" is always a known pill (the group mention).
  const mentionAlts = useMemo(() => {
    const names = new Set<string>(['crew']);
    for (const p of participants) {
      const full = p.display_name.trim();
      if (!full) continue;
      names.add(full);
      const first = full.split(/\s+/)[0]!;
      if (first && first !== full) names.add(first);
    }
    return [...names].sort((a, b) => b.length - a.length).map(escapeRegExp).join('|');
  }, [participants]);

  function renderBody(body: string): ReactNode {
    if (!mentionAlts) return body;
    const re = new RegExp(`(^|\\s)(@(?:${mentionAlts}))`, 'gi');
    const out: ReactNode[] = [];
    let last = 0;
    let key = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(body)) !== null) {
      const start = m.index + m[1]!.length;
      if (start > last) out.push(body.slice(last, start));
      out.push(
        <span
          key={key++}
          className="rounded bg-accent/10 px-1 font-medium text-accent"
        >
          {m[2]}
        </span>,
      );
      last = start + m[2]!.length;
    }
    if (out.length === 0) return body;
    if (last < body.length) out.push(body.slice(last));
    return out;
  }

  /** Replace the active "@token" with `insert` and restore focus past it. */
  function replaceToken(insert: string) {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after = text.slice(caret);
    const next = `${before}${insert}${after}`;
    setText(next);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const pos = before.length + insert.length;
      el.focus();
      el.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  }

  function applyMention(p: TaskMessageParticipant) {
    replaceToken(`@${p.display_name} `);
    setPicked((prev) => new Set(prev).add(p.user_id));
  }

  /** Insert the "@crew" group mention. */
  function applyCrew() {
    replaceToken('@crew ');
  }

  /** Pin a project document as a pending attachment (drops the "@token"). */
  function applyDoc(doc: (typeof projectDocs)[number]) {
    setPendingAtts((prev) =>
      prev.some((a) => a.id === doc.id)
        ? prev
        : [
            ...prev,
            {
              id: doc.id,
              filename: doc.original_filename,
              is_image: (doc.mime_type ?? '').startsWith('image/'),
              file_url: doc.file_url,
            },
          ],
    );
    replaceToken(''); // the doc rides as a chip, not as body text
  }

  function removePending(id: string) {
    setPendingAtts((prev) => prev.filter((a) => a.id !== id));
  }

  /** Upload picked files to the task and pin them as pending attachments. */
  async function onPickFiles(files: FileList | null) {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const file of list) {
        const att = await uploadChatMedia(scope, file);
        setPendingAtts((prev) => [
          ...prev,
          {
            id: att.id,
            filename: att.original_filename,
            is_image: (att.mime_type ?? '').startsWith('image/'),
            file_url: att.file_url,
          },
        ]);
      }
    } catch (err) {
      toast.error(`Couldn't attach: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  async function submit() {
    const body = text.trim();
    const attachmentIds = pendingAtts.map((a) => a.id);
    // Need either text or at least one attachment to post.
    if ((!body && attachmentIds.length === 0) || send.isPending) return;
    // Only keep picks whose name still appears in the body (the user may have
    // edited it back out); the server re-parses regardless, this just trims.
    const mentioned = participants
      .filter((p) => picked.has(p.user_id) && body.includes(`@${p.display_name}`))
      .map((p) => p.user_id);
    try {
      await send.mutateAsync({
        // A body is required server-side; for an attachment-only post send a
        // single space so the message still carries its files.
        body: body || ' ',
        mentioned_user_ids: mentioned.length > 0 ? mentioned : undefined,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
      setText('');
      setPicked(new Set());
      setPendingAtts([]);
      setCaret(0);
    } catch (err) {
      toast.error(`Couldn't post: ${(err as Error).message}`);
    }
  }

  return (
    <div
      id="crew-chat"
      className={cn('bg-bg rounded-2xl border border-line p-4 space-y-3', className)}
    >
      {/* Make the crew-only scope unmistakable — this is NOT the client feed. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-ink-faint">
          Crew chat
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-fill-soft px-2 py-0.5 text-[10px] font-medium text-ink-dim">
          <Lock className="h-3 w-3" />
          Vari crew only
        </span>
      </div>
      <p className="text-xs text-ink-faint -mt-1">
        Internal messages for the crew and supervisors on this task. The
        requester doesn’t see these. <span className="whitespace-nowrap">@mention</span> a
        teammate (or <span className="whitespace-nowrap">@crew</span>) to email them a link here.
      </p>

      {/* Message list — newest-last. */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-ink-faint py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : messages.length === 0 ? (
        <div className="text-sm text-ink-faint py-3">
          No messages yet — start the conversation below.
        </div>
      ) : (
        <ul className="space-y-3">
          {messages.map((m) => {
            const mine = !!me?.id && m.author_user_id === me.id;
            return (
              <li key={m.id} className="flex items-start gap-2">
                <Avatar
                  name={m.author_display_name}
                  src={m.author_avatar_url}
                  className="h-7 w-7 text-[9px]"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">
                      {mine ? 'You' : m.author_display_name}
                    </span>
                    <span className="text-[11px] text-ink-faint">
                      {format(parseISO(m.created_at), 'MMM d, h:mma').toLowerCase()}
                    </span>
                  </div>
                  {m.body.trim() && (
                    <div className="text-sm text-ink-dim whitespace-pre-wrap break-words">
                      {renderBody(m.body)}
                    </div>
                  )}
                  {m.attachments.length > 0 && (
                    <MessageAttachments attachments={m.attachments} />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Composer with @mention autocomplete. */}
      <div className="relative">
        {/* Pending attachments (uploaded files + @-referenced docs) — removable. */}
        {pendingAtts.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-1.5">
            {pendingAtts.map((a) => (
              <li
                key={a.id}
                className="inline-flex max-w-[14rem] items-center gap-1.5 rounded border border-line bg-fill-soft px-2 py-1 text-xs"
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
                <span className="truncate">{a.filename}</span>
                <button
                  type="button"
                  onClick={() => removePending(a.id)}
                  aria-label={`Remove ${a.filename}`}
                  className="shrink-0 text-ink-faint hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyUp={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
          onClick={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter newlines. Don't send while a suggestion
            // list is open (Enter there is "accept the highlighted name").
            if (e.key === 'Enter' && !e.shiftKey && !hasSuggestions) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={2}
          placeholder="Message the crew… use @ to mention or attach a doc"
          className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
        />

        {hasSuggestions && (
          <ul
            role="listbox"
            aria-label="Mention a teammate or attach a document"
            className="absolute left-0 right-0 bottom-full mb-1 z-10 max-h-72 overflow-auto rounded-lg border border-line bg-bg shadow-lg"
          >
            {/* @crew — group mention, always first when offered. */}
            {offerCrew && (
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyCrew();
                  }}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-fill-soft"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Users className="h-3.5 w-3.5" />
                  </span>
                  <span className="flex-1 truncate font-medium">@crew</span>
                  <span className="text-[10px] uppercase tracking-wide text-ink-faint">
                    Everyone on this task
                  </span>
                </button>
              </li>
            )}

            {/* People. */}
            {suggestions.map((p) => (
              <li key={p.user_id}>
                <button
                  type="button"
                  // onMouseDown (not onClick) so the textarea doesn't blur first.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyMention(p);
                  }}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-fill-soft"
                >
                  <Avatar name={p.display_name} src={p.avatar_url} className="h-6 w-6 text-[9px]" />
                  <span className="flex-1 truncate">{p.display_name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-ink-faint">
                    {roleHint(p.role)}
                  </span>
                </button>
              </li>
            ))}

            {/* Project documents — picking one pins it as a chip (not body text). */}
            {docSuggestions.length > 0 && (
              <>
                <li className="border-t border-line px-2.5 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-ink-faint">
                  Documents
                </li>
                {docSuggestions.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        applyDoc(d);
                      }}
                      className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-fill-soft"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-ink-faint" />
                      <span className="flex-1 truncate">{d.original_filename}</span>
                    </button>
                  </li>
                ))}
              </>
            )}
          </ul>
        )}

        {/* Hidden inputs: the paperclip picks files (image/video/PDF); the camera
            one opens the device camera straight away on mobile (capture) — one tap
            to shoot a photo or video for the crew. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            void onPickFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            void onPickFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <div className="mt-2 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
            >
              <Camera className="h-3.5 w-3.5" />
              Camera
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )}
              Attach
            </Button>
            <span className="inline-flex items-center gap-1 text-[11px] text-ink-faint">
              <AtSign className="h-3 w-3" />
              Mention to notify
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={(!text.trim() && pendingAtts.length === 0) || send.isPending}
          >
            {send.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Download URL for a chat attachment — prefers its signed `file_url`. */
function attUrl(a: TaskMessageAttachment): string {
  return attachmentFileUrl({ id: a.id, file_url: a.file_url ?? undefined });
}

/** Unified attachment rendering under a message: image thumbs + file/doc chips. */
function MessageAttachments({ attachments }: { attachments: TaskMessageAttachment[] }) {
  const images = attachments.filter((a) => a.is_image);
  const files = attachments.filter((a) => !a.is_image);
  return (
    <div className="mt-1.5 space-y-1.5">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((a) => (
            <a key={a.id} href={attUrl(a)} target="_blank" rel="noreferrer" className="block">
              <img
                src={attUrl(a)}
                alt={a.filename}
                className="h-20 w-20 rounded-lg border border-line object-cover"
              />
            </a>
          ))}
        </div>
      )}
      {files.map((a) => (
        <a
          key={a.id}
          href={attUrl(a)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-full items-center gap-1.5 rounded border border-line bg-fill-soft px-2 py-1 text-xs text-ink-dim hover:bg-fill"
        >
          <FileText className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
          <span className="truncate">{a.filename}</span>
        </a>
      ))}
    </div>
  );
}
